import { ReactElement } from 'react'
import { I18N, I18NValidValues } from 'typings/custom'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import translations, { StringsDictionary } from 'lang'
import { logError } from 'helpers/log'

export const language = getLanguageFromStorage()

const i18n: I18N = (lang, data, dict = translations) => {
  const strings = dict[language] as StringsDictionary
  const messageScope = Object.keys(lang)[0]
  const messageKey = lang[messageScope]

  // Log lookup issue for better developer quality of life 😁
  if (
    !strings[messageScope]?.[messageKey] &&
    strings[messageScope]?.[messageKey] !== ''
  ) {
    logError(`😭 No translation found for "${JSON.stringify(lang)}"`)
    // Return a empty string so any pre-existing code continues to "fail quietly"
    return ''
  }

  const message = strings?.[messageScope]?.[messageKey]

  let result: string | ReactElement

  if (typeof message === 'function') {
    result = message(data as Record<string, unknown>)
  } else if (typeof data === 'object') {
    // interpolate data in the translation string
    result = message
    const args = Object.keys(data)
    args.forEach((arg: string) => {
      result = (result as string).replaceAll(`%${arg}%`, `${data[arg]}`)
    })
  } else if (typeof data === 'string') {
    // interpolate data in the translation string only for one occurrence
    result = (message as string).replace(/%.+%/i, data)
  }

  return result || (message as string)
}

export const translateError = (key: string) => i18n({ errors: key }) as string

/**
 * Helper function that takes one member of translation dictionary as argument
 * it helps to write cleaner code
 * @param member Member of translation dict containing the key.
 * @returns function - simplified i18 translation function
 */
export const translationMember = (member: string) => {
  /**
   * Simplified version of i18n function
   * @param { string } key The lookup key for the translation dict
   * @param { object | string } replaceValue - object containing key and value to replace, use string if text only contains one replacing key
   * @returns translation - string or ReactElement, string type by default, set ReactElement generic type otherwise
   */
  return function t<R = string>(
    key: string,
    replaceValue?: Record<string, I18NValidValues> | I18NValidValues
  ): R {
    return i18n({ [member]: key }, replaceValue) as R
  }
}

export default i18n
